import React from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  Mail,
  MapPin,
  Globe,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  MessageCircle,
  ArrowUpRight,
  Clock,
} from "lucide-react";
import { NAV_LINKS } from "@/lib/constants";
import { useSettings } from "@/lib/SiteContent";
import { openCookieBanner } from "@/lib/cookieConsent";

const SocialIconLink = ({ href, label, Icon, testId }) => (
  <a
    href={href || "#"}
    aria-label={label}
    target="_blank"
    rel="noreferrer"
    data-testid={testId}
    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/12 text-white/75 hover:text-[color:var(--sp-gold)] hover:border-[color:rgba(212,175,55,0.55)] hover:bg-[color:rgba(212,175,55,0.06)] transition-all duration-300"
  >
    <Icon className="h-4 w-4" />
  </a>
);

const InfoRow = ({ Icon, children }) => (
  <li className="flex items-start gap-3 text-sm text-white/75">
    <span className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full border border-[color:rgba(212,175,55,0.35)] bg-black/40 flex-shrink-0">
      <Icon className="h-3.5 w-3.5 text-[color:var(--sp-gold)]" />
    </span>
    <span className="leading-relaxed">{children}</span>
  </li>
);

const Footer = () => {
  const year = new Date().getFullYear();
  const settings = useSettings();
  const contact = settings?.contact || {};
  const social = settings?.social || {};
  const brand = settings?.brand || {};
  const whatsapp = contact.whatsapp || "+91 96372 41414";
  const whatsappDigits = contact.whatsappDigits || "919637241414";
  const email = contact.email || "info@seetaiproperties.com";
  const website = contact.website || "www.seetaiproperties.com";
  const headquarters =
    contact.headquarters || "Pune & Pandharpur, Maharashtra, India";
  const siteAddress =
    contact.siteAddress ||
    "Airport Link Road, Jejuri Road, Bhandgaon, Maharashtra 412214";

  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-white/10 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a] to-[#070707] text-white overflow-hidden"
    >
      {/* Gold rule */}
      <div className="gold-rule" />

      {/* CTA strip */}
      <section
        data-testid="footer-cta-strip"
        className="relative border-b border-white/8"
      >
        <div className="container-narrow py-10 md:py-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <p className="text-[10px] tracking-[0.36em] uppercase text-[color:var(--sp-gold)]">
              <span className="gold-divider" /> Plan Your Visit
            </p>
            <h3 className="font-display text-2xl md:text-3xl mt-3 leading-tight max-w-xl">
              Experience Silva City in person.
            </h3>
            <p className="mt-2 text-white/65 text-sm max-w-xl">
              Book a private guided site visit with our advisory team. Weekday
              and weekend slots available.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 sm:flex-nowrap">
            <Link
              to="/contact"
              data-testid="footer-cta-book-visit"
              className="btn-gold inline-flex items-center justify-center gap-2 px-6 py-3 text-[11px] tracking-[0.22em] uppercase whitespace-nowrap"
            >
              Book a Site Visit <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a
              href={`https://wa.me/${whatsappDigits}?text=${encodeURIComponent(
                "Hello Seetai Properties, I'd like to schedule a Silva City site visit."
              )}`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              data-testid="footer-cta-whatsapp"
              className="btn-ghost inline-flex items-center justify-center gap-2 px-6 py-3 text-[11px] tracking-[0.22em] uppercase whitespace-nowrap"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* Main content */}
      <div className="container-narrow pt-14 pb-12">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-12">
          {/* Brand block */}
          <div className="lg:col-span-4 md:col-span-2">
            <Link
              to="/"
              data-testid="footer-brand-home"
              aria-label="Seetai Properties Home"
              className="inline-block"
            >
              <img
                src="/brand/seetai-logo-full.png"
                alt="Seetai Properties — Crafting Luxury. Building Legacy."
                loading="lazy"
                decoding="async"
                draggable={false}
                className="block h-14 w-auto select-none"
              />
            </Link>
            <p className="mt-5 text-sm text-white/65 leading-relaxed max-w-sm">
              {brand.legalName || "Seetai Properties Private Limited"} crafts
              visionary townships and premium real-estate experiences that
              endure across generations.
            </p>
            <p className="mt-4 text-[10px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
              {brand.tagline || "Crafting Luxury. Building Legacy."}
            </p>

            <div className="mt-6 flex items-center gap-2">
              <SocialIconLink
                href={social.instagram}
                label="Instagram"
                Icon={Instagram}
                testId="footer-social-instagram"
              />
              <SocialIconLink
                href={social.facebook}
                label="Facebook"
                Icon={Facebook}
                testId="footer-social-facebook"
              />
              <SocialIconLink
                href={social.linkedin}
                label="LinkedIn"
                Icon={Linkedin}
                testId="footer-social-linkedin"
              />
              <SocialIconLink
                href={social.youtube}
                label="YouTube"
                Icon={Youtube}
                testId="footer-social-youtube"
              />
            </div>
          </div>

          {/* Explore */}
          <div className="lg:col-span-2">
            <h4 className="font-display text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)] mb-5">
              <span className="gold-divider" /> Explore
            </h4>
            <ul className="space-y-3 text-sm text-white/75">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="inline-flex items-center gap-2 group hover:text-[color:var(--sp-gold)] transition-colors"
                  >
                    <span className="h-1 w-1 rounded-full bg-white/30 group-hover:bg-[color:var(--sp-gold)] transition-colors" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <h4 className="font-display text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)] mb-5">
              <span className="gold-divider" /> Reach Us
            </h4>
            <ul className="space-y-4">
              <InfoRow Icon={Phone}>
                <a
                  href={`tel:${whatsappDigits}`}
                  data-testid="footer-info-phone"
                  className="hover:text-[color:var(--sp-gold)] transition-colors"
                >
                  {whatsapp}
                </a>
              </InfoRow>
              <InfoRow Icon={MessageCircle}>
                <a
                  href={`https://wa.me/${whatsappDigits}`}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  data-testid="footer-info-whatsapp"
                  className="hover:text-[color:var(--sp-gold)] transition-colors"
                >
                  Chat on WhatsApp
                </a>
              </InfoRow>
              <InfoRow Icon={Mail}>
                <a
                  href={`mailto:${email}`}
                  data-testid="footer-info-email"
                  className="hover:text-[color:var(--sp-gold)] transition-colors break-all"
                >
                  {email}
                </a>
              </InfoRow>
              <InfoRow Icon={Globe}>
                <span>{website}</span>
              </InfoRow>
              <InfoRow Icon={Clock}>
                <span>
                  {contact.officeHours || "Mon – Sat · 10 AM – 7 PM IST"}
                </span>
              </InfoRow>
            </ul>
          </div>

          {/* Locations */}
          <div className="lg:col-span-3">
            <h4 className="font-display text-[11px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)] mb-5">
              <span className="gold-divider" /> Our Locations
            </h4>
            <ul className="space-y-5">
              <li>
                <p className="text-[10px] tracking-[0.28em] uppercase text-white/55">
                  Headquarters
                </p>
                <div className="mt-1.5 flex items-start gap-2.5">
                  <MapPin className="h-4 w-4 text-[color:var(--sp-gold)] mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-white/80 leading-relaxed">
                    {headquarters}
                  </p>
                </div>
              </li>
              <li>
                <p className="text-[10px] tracking-[0.28em] uppercase text-white/55">
                  Silva City Site
                </p>
                <div className="mt-1.5 flex items-start gap-2.5">
                  <MapPin className="h-4 w-4 text-[color:var(--sp-gold)] mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-white/80 leading-relaxed">
                    {siteAddress}
                  </p>
                </div>
                <a
                  href="https://www.google.com/maps?q=18.4125,74.325306&z=15"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-cta-directions"
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-[color:rgba(212,175,55,0.5)] bg-[color:var(--sp-gold-soft)] hover:bg-[color:var(--sp-gold)] hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:rgba(212,175,55,0.6)] px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase font-semibold whitespace-nowrap text-[color:var(--sp-gold)] transition-colors no-gold-shine"
                >
                  Get Directions <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom — 3 centred rows */}
        <div className="gold-rule mt-12" />

        {/* Row 1 — Legal nav (Terms · Privacy · Cookie Preferences) — centered */}
        <nav
          aria-label="Legal"
          className="pt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-white/55"
        >
          <Link
            to="/terms"
            data-testid="footer-link-terms"
            className="tracking-[0.2em] uppercase text-white/65 hover:text-[color:var(--sp-gold)] transition-colors whitespace-nowrap"
          >
            Terms & Conditions
          </Link>
          <span aria-hidden="true" className="text-white/15">·</span>
          <Link
            to="/privacy"
            data-testid="footer-link-privacy"
            className="tracking-[0.2em] uppercase text-white/65 hover:text-[color:var(--sp-gold)] transition-colors whitespace-nowrap"
          >
            Privacy & Security
          </Link>
          <span aria-hidden="true" className="text-white/15">·</span>
          <button
            type="button"
            onClick={openCookieBanner}
            data-testid="footer-link-cookie-preferences"
            className="tracking-[0.2em] uppercase text-white/65 hover:text-[color:var(--sp-gold)] transition-colors whitespace-nowrap"
          >
            Cookie Preferences
          </button>
        </nav>

        {/* Row 2 — Copyright — centered */}
        <p
          data-testid="footer-copyright"
          className="mt-4 text-center text-xs text-white/55"
        >
          © {year} {brand.legalName || "Seetai Properties Private Limited"}. All rights reserved.
        </p>

        {/* Row 3 — Tagline — centered, bold, golden, animated shine.
            The wrapper is text-center so that the inline-block <p>
            (gold-shine forces inline-block) is horizontally centered
            within the container at every viewport. */}
        <div className="mt-5 w-full text-center px-2 overflow-hidden">
          <p
            data-testid="footer-tagline"
            className="text-[9px] sm:text-[12px] md:text-[15px] lg:text-[17px] tracking-[0.24em] sm:tracking-[0.28em] md:tracking-[0.32em] uppercase font-bold text-[color:var(--sp-gold)] whitespace-nowrap"
          >
            Crafted with intent · Built for legacy
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
